import { type HubConnection, HubConnectionBuilder } from "@microsoft/signalr";
import { useQueryClient } from "@tanstack/react-query";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { splitsKeys } from "@/shared/lib/queryKeys";
import {
  CURSOR_STALE_MS,
  CURSOR_THROTTLE_MS,
  splitsHubUrl,
} from "../splits.config";

export type RealtimeStatus =
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected";

// Hub -> client presence entry. mainClass is a WoW class db-string, or null (no main character).
export interface PresenceMember {
  connectionId: string;
  userId: string;
  label: string;
  mainClass: string | null;
}

// Hub -> client cursor frame, tagged locally with arrival time for staleness eviction.
export interface RemoteCursor {
  x: number;
  y: number;
  draggedCharacterId: string | null;
  lastUpdate: number;
}

// Client -> hub cursor frame. Coordinates are board-relative fractions (0..1).
export interface CursorPayload {
  x: number;
  y: number;
  draggedCharacterId: string | null;
}

interface CursorState {
  connectionId: string;
  x: number;
  y: number;
  draggedCharacterId: string | null;
}

export interface SplitsRealtime {
  status: RealtimeStatus;
  members: Map<string, PresenceMember>;
  cursors: Map<string, RemoteCursor>;
  sendCursor: (payload: CursorPayload) => void;
  hideCursor: () => void;
}

// Immutably drop a key from a Map held in state, leaving the reference untouched when absent.
const removeFrom = <V>(
  set: Dispatch<SetStateAction<Map<string, V>>>,
  key: string,
) =>
  set((prev) => {
    if (!prev.has(key)) return prev;
    const next = new Map(prev);
    next.delete(key);
    return next;
  });

const sameCursor = (a: CursorPayload, b: CursorPayload | null) =>
  b !== null &&
  a.x === b.x &&
  a.y === b.y &&
  a.draggedCharacterId === b.draggedCharacterId;

// Presence + cursor relay for the splits board. The socket carries only ephemeral data; the REST
// GET stays the source of truth. A "board changed" nudge (and every (re)connect) invalidates the
// splits query so the board refetches. Connection loss is surfaced via `status`, never thrown into
// render (per CLAUDE.md error handling — the banner handles it, not the ErrorDialog).
export function useSplitsRealtime(): SplitsRealtime {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<RealtimeStatus>("connecting");
  const [members, setMembers] = useState<Map<string, PresenceMember>>(
    () => new Map(),
  );
  const [cursors, setCursors] = useState<Map<string, RemoteCursor>>(
    () => new Map(),
  );

  const connectionRef = useRef<HubConnection | null>(null);
  // Trailing throttle: latest pending frame is flushed at most every CURSOR_THROTTLE_MS, and only
  // when it differs from the last sent frame.
  const pendingRef = useRef<CursorPayload | null>(null);
  const lastSentRef = useRef<CursorPayload | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const conn = new HubConnectionBuilder()
      .withUrl(splitsHubUrl(), { withCredentials: true })
      .withAutomaticReconnect()
      .build();
    connectionRef.current = conn;

    conn.on("Roster", (list: PresenceMember[]) =>
      setMembers(new Map(list.map((m) => [m.connectionId, m]))),
    );
    conn.on("Joined", (m: PresenceMember) =>
      setMembers((prev) => new Map(prev).set(m.connectionId, m)),
    );
    conn.on("Left", (connectionId: string) => {
      removeFrom(setMembers, connectionId);
      removeFrom(setCursors, connectionId);
    });
    conn.on("Cursor", (c: CursorState) =>
      setCursors((prev) =>
        new Map(prev).set(c.connectionId, {
          x: c.x,
          y: c.y,
          draggedCharacterId: c.draggedCharacterId,
          lastUpdate: Date.now(),
        }),
      ),
    );
    conn.on("CursorHidden", (connectionId: string) =>
      removeFrom(setCursors, connectionId),
    );
    conn.on("BoardChanged", () =>
      queryClient.invalidateQueries({ queryKey: splitsKeys.all }),
    );

    conn.onreconnecting(() => setStatus("reconnecting"));
    // Reconnect-refetch is the linchpin that closes the "missed a nudge across a reconnect" gap.
    conn.onreconnected(() => {
      setStatus("connected");
      queryClient.invalidateQueries({ queryKey: splitsKeys.all });
    });
    conn.onclose(() => setStatus("disconnected"));

    let cancelled = false;
    setStatus("connecting");
    conn
      .start()
      .then(() => {
        if (cancelled) return;
        setStatus("connected");
        queryClient.invalidateQueries({ queryKey: splitsKeys.all });
      })
      .catch(() => {
        if (!cancelled) setStatus("disconnected");
      });

    return () => {
      cancelled = true;
      conn.stop().catch(() => {});
      connectionRef.current = null;
    };
  }, [queryClient]);

  // Belt-and-suspenders eviction of cursors we stopped hearing from (missed Left/CursorHidden).
  useEffect(() => {
    const id = setInterval(() => {
      const cutoff = Date.now() - CURSOR_STALE_MS;
      setCursors((prev) => {
        // Clone lazily — the common case (nothing stale) keeps the same reference and skips re-render.
        let next: Map<string, RemoteCursor> | null = null;
        for (const [key, cursor] of prev) {
          if (cursor.lastUpdate < cutoff) {
            next ??= new Map(prev);
            next.delete(key);
          }
        }
        return next ?? prev;
      });
    }, 2000);
    return () => clearInterval(id);
  }, []);

  const flush = useCallback(() => {
    timerRef.current = null;
    const payload = pendingRef.current;
    pendingRef.current = null;
    if (!payload || sameCursor(payload, lastSentRef.current)) return;
    lastSentRef.current = payload;
    connectionRef.current?.invoke("UpdateCursor", payload).catch(() => {});
  }, []);

  const sendCursor = useCallback(
    (payload: CursorPayload) => {
      pendingRef.current = payload;
      if (timerRef.current === null) {
        timerRef.current = setTimeout(flush, CURSOR_THROTTLE_MS);
      }
    },
    [flush],
  );

  const hideCursor = useCallback(() => {
    pendingRef.current = null;
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    lastSentRef.current = null;
    connectionRef.current?.invoke("HideCursor").catch(() => {});
  }, []);

  return { status, members, cursors, sendCursor, hideCursor };
}
